/**
 * 音声操作列の共通コンポーネント (SBI-009)。
 *
 *   [Audio プレイヤー] [変更内容でAudio再作成] [ダウンロード]
 *   (未反映の注記 / 追加メッセージ / 音声エラー)
 *
 * Step1 / Step2 (`StepAudioControls`) と Step3 (`SampleCandidateCard`) の 3 箇所で使う。
 * 従来は同じ JSX を 2 か所に書いており、見た目の修正が片方だけに入る恐れがあった。
 *
 * ## ⚠️ 描画だけを担う (状態を持たない)
 *
 * 共通化したのは **描画と、描画に使う小さな判定** だけ。次は呼び出し側に残す。
 * 状態の持ち方が Step1/2 と Step3 で違い、無理に揃えると片方を壊すため。
 *
 *   - 音声の合成と状態管理
 *       - Step1 / Step2 … `StepAudioControls` が `useSsmlAudio` で 1 件持つ
 *       - Step3         … 親 (`Step3SamplePanel`) が候補単位で持つ
 *   - 再作成ボタンを押せる条件と、押せない理由の文言
 *   - 利用ログ `audio_downloaded` (Step3 固有 / `download.onDownloaded` で通知だけ受ける)
 *   - 候補番号付きの `data-testid` (`testIds` で呼び出し側が決める)
 *
 * ## 約束 (全 Step 共通)
 *
 * - **ダウンロードは再生と同じ URL** を `<a download>` に渡す。保存のために
 *   `/audio-preview` を呼び直さない (Polly の二重課金と、聴いた音と保存した音のずれを防ぐ)。
 * - ダウンロードの通知 (`onDownloaded`) では `preventDefault()` しない。保存を止めない。
 * - 音声が無い間も **無効なダウンロードボタンを残す** (消すと「保存できない仕様」と誤解される)。
 * - URL が無い状態で `<a download href="">` を出さない (押した瞬間に画面が再読み込みされる)。
 * - 自動再生しない (複数の音声が同時に鳴って聴き比べにならない)。
 */
import { Spinner } from '../Spinner';
import type React from 'react';

import { getReadyAudioUrl, isAudioSynthesizing } from './audioControlsUtils';
import type { AudioControlsTestIds } from './audioControlsUtils';
import type { SsmlAudioState } from './useSsmlAudio';

/** 再作成ボタンの名称 (全 Step 共通の文言 / SBI-002)。 */
export const RECREATE_AUDIO_BUTTON_LABEL = '変更内容でAudio再作成';

/** ダウンロードボタンの名称 (全 Step 共通の文言)。 */
export const DOWNLOAD_AUDIO_BUTTON_LABEL = 'ダウンロード';

/** 音声が無い間の状態表示 (生成中)。`pending` / `synthesizing` は利用者には区別しない。 */
const SYNTHESIZING_LABEL = '音声を生成中…';

/** 音声が無い間の状態表示 (未作成)。 */
const NOT_CREATED_LABEL = '音声は未作成です';

export interface AudioControlsRecreate {
  /** 押したときの処理 */
  onClick: () => void;
  /** 押せない状態か */
  disabled: boolean;
  /** ホバーで見せる理由 (押せるときや理由を出さないときは省略) */
  title?: string;
  /** 理由文の要素 ID (`aria-describedby` の参照先) */
  describedBy?: string;
}

export interface AudioControlsDownload {
  /** 保存名 (`遅延案内_駅名強調.mp3`)。音声が無い間も渡す。 */
  fileName: string;
  /**
   * ダウンロードが押されたことの通知 (Step3 の利用ログ `audio_downloaded` 用)。
   * ⚠️ 通知だけ。`preventDefault()` も再合成もしない。
   */
  onDownloaded?: () => void;
}

export interface AudioControlsProps {
  /** 音声の状態 (`undefined` は未着手)。 */
  audio: SsmlAudioState | undefined;
  /** 再作成ボタン。`null` なら出さない (押せるのに動かない UI を置かない)。 */
  recreate: AudioControlsRecreate | null;
  /** ダウンロード */
  download: AudioControlsDownload;
  /** 編集済みだが再作成していない (「編集内容は音声に反映されていません」を出す)。 */
  isDirty: boolean;
  /** `data-testid` の一覧 */
  testIds: AudioControlsTestIds;
  /**
   * 注記とエラーの間に出す呼び出し側固有のメッセージ
   * (押せない理由 / 再作成の前段で弾いた理由など)。
   */
  children?: React.ReactNode;
}

export function AudioControls({
  audio,
  recreate,
  download,
  isDirty,
  testIds,
  children,
}: AudioControlsProps): React.JSX.Element {
  const audioUrl = getReadyAudioUrl(audio);
  const isSynthesizing = isAudioSynthesizing(audio);
  /*
   * SBI-010: 未反映 (Dirty) で **押せる** ときだけ、再作成ボタンを橙系で強調する。
   * ⚠️ 生成中 (`disabled`) は強調しない: 生成中は灰色の無効表示 (`:disabled`) のままにして、
   *   「未反映の警告」と「処理中」を取り違えない。
   */
  const emphasizeRecreate = isDirty && recreate !== null && !recreate.disabled;

  return (
    <div className="ssml-audio-controls" data-testid={testIds.root}>
      <div className="ssml-audio-controls__row">
        {audioUrl !== null ? (
          <audio
            className="ssml-audio-controls__player"
            data-testid={testIds.player}
            src={audioUrl}
            controls
            /* ⚠️ autoPlay を付けない: 複数候補が同時に鳴ると聴き比べにならない。 */
          />
        ) : (
          /*
            プレイヤーの代わりの状態表示 (空欄にすると「音声が作られない」と誤解される)。
            `pending` と `synthesizing` は利用者から見て区別がつかないため同じ表示にする
            (内部状態としては分けて二重呼び出しを防ぐ)。
          */
          <span
            className="ssml-audio-controls__status"
            data-testid={
              isSynthesizing && testIds.pending !== undefined ? testIds.pending : testIds.status
            }
          >
            {isSynthesizing ? (
              <>
                <Spinner />
                {SYNTHESIZING_LABEL}
              </>
            ) : (
              NOT_CREATED_LABEL
            )}
          </span>
        )}

        {recreate !== null && (
          <button
            type="button"
            className={
              emphasizeRecreate
                ? 'ssml-tuning-button ssml-audio-controls__button ssml-audio-controls__button--dirty'
                : 'ssml-tuning-button ssml-audio-controls__button'
            }
            data-testid={testIds.recreate}
            onClick={recreate.onClick}
            disabled={recreate.disabled}
            title={recreate.title}
            aria-describedby={recreate.describedBy}
          >
            {RECREATE_AUDIO_BUTTON_LABEL}
          </button>
        )}

        {audioUrl !== null ? (
          <a
            className="ssml-tuning-button ssml-download-button"
            data-testid={testIds.download}
            /* ⚠️ 再生と同じ URL。保存のために /audio-preview を呼び直さない。 */
            href={audioUrl}
            download={download.fileName}
            /* ⚠️ 通知だけ (`preventDefault()` を呼ばない)。ここで止めると保存できなくなる。 */
            onClick={download.onDownloaded}
          >
            {DOWNLOAD_AUDIO_BUTTON_LABEL}
          </a>
        ) : (
          /*
            音声が無い間は **無効なボタン** として出す。
            ⚠️ 要素を消すと「保存できない仕様」と誤解される。`<a>` のまま無効化はできない
            (無効の a はクリックを止められない) ため、`<button disabled>` に置き換える。
          */
          <button
            type="button"
            className="ssml-tuning-button ssml-download-button"
            data-testid={testIds.download}
            disabled
            title="音声が作成されるとダウンロードできます"
          >
            {DOWNLOAD_AUDIO_BUTTON_LABEL}
          </button>
        )}
      </div>

      {/*
        ⚠️ 「編集したのに再作成していない」ことを明示する。
        これが無いと、編集前の音声を聴き続ける (最も気づきにくい誤解)。
      */}
      {isDirty && (
        <p className="ssml-audio-controls__dirty" data-testid={testIds.dirty}>
          編集内容は音声に反映されていません
        </p>
      )}

      {children}

      {/* 合成の失敗はサーバのメッセージをそのまま出す (SSML 構文エラーの 400 など)。 */}
      {audio?.status === 'error' && (
        <p className="ssml-tuning-error" role="alert" data-testid={testIds.error}>
          {audio.error ?? '音声の生成に失敗しました'}
        </p>
      )}
    </div>
  );
}
